// Eş portföyü için manuel lot girişi formu (Task 11).
//
// UX: kompakt, dikey form. Submit'te POST /spouse/lots → success'te form reset
// + parent callback (listeye optimistic ekleme yapsın). 400/unknown_fund
// hatasında inline mesaj.

import { useState, type FormEvent } from "react";
import { createSpouseLot, ApiError, type ApiConfig } from "../lib/api";
import type { SpouseLotCreateResponse } from "../types/api";

interface Props {
  cfg: ApiConfig;
  onCreated: (resp: SpouseLotCreateResponse) => void;
}

interface FormState {
  fund_code: string;
  purchase_date: string;
  units: string;
  cost_per_unit: string;
  note: string;
}

const today = (): string => new Date().toISOString().slice(0, 10);

const EMPTY: FormState = {
  fund_code: "",
  purchase_date: today(),
  units: "",
  cost_per_unit: "",
  note: "",
};

export default function EsLotForm({ cfg, onCreated }: Props) {
  const [s, setS] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  function set<K extends keyof FormState>(k: K, v: string): void {
    setS((prev) => ({ ...prev, [k]: v }));
    setErr(null);
    setOk(null);
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    setOk(null);

    const units = Number(s.units.replace(",", "."));
    const cost = Number(s.cost_per_unit.replace(",", "."));
    if (!s.fund_code.trim()) {
      setErr("Fon kodu girilmeli.");
      return;
    }
    if (!Number.isFinite(units) || units <= 0) {
      setErr("Adet pozitif sayı olmalı.");
      return;
    }
    if (!Number.isFinite(cost) || cost <= 0) {
      setErr("Maliyet pozitif sayı olmalı.");
      return;
    }
    if (!s.purchase_date) {
      setErr("Alış tarihi girilmeli.");
      return;
    }

    setSubmitting(true);
    try {
      const resp = await createSpouseLot(cfg, {
        fund_code: s.fund_code.trim().toUpperCase(),
        purchase_date: s.purchase_date,
        units,
        cost_per_unit: cost,
        note: s.note.trim() || undefined,
      });
      onCreated(resp);
      if (resp.already_exists) {
        setOk("Bu lot zaten kayıtlı (aynı fon, tarih, adet, maliyet).");
      } else {
        setOk("Lot eklendi.");
      }
      setS(EMPTY);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : String(e);
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl bg-ink-800 border border-ink-700 p-4 space-y-3"
      aria-label="Eş lot girişi"
    >
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">
            Fon kodu
          </span>
          <input
            type="text"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            value={s.fund_code}
            onChange={(e) => set("fund_code", e.target.value)}
            placeholder="örn. TLY"
            maxLength={6}
            className="mt-1 w-full bg-ink-900 border border-ink-700 focus:border-accent-500 rounded-md px-2 py-1.5 text-ink-100 text-sm fon-dili uppercase"
            disabled={submitting}
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">
            Alış tarihi
          </span>
          <input
            type="date"
            value={s.purchase_date}
            max={today()}
            onChange={(e) => set("purchase_date", e.target.value)}
            className="mt-1 w-full bg-ink-900 border border-ink-700 focus:border-accent-500 rounded-md px-2 py-1.5 text-ink-100 text-sm fon-dili"
            disabled={submitting}
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">
            Adet
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={s.units}
            onChange={(e) => set("units", e.target.value)}
            placeholder="örn. 100"
            className="mt-1 w-full bg-ink-900 border border-ink-700 focus:border-accent-500 rounded-md px-2 py-1.5 text-ink-100 text-sm fon-dili text-right"
            disabled={submitting}
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">
            Maliyet (NAV)
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={s.cost_per_unit}
            onChange={(e) => set("cost_per_unit", e.target.value)}
            placeholder="örn. 12,345"
            className="mt-1 w-full bg-ink-900 border border-ink-700 focus:border-accent-500 rounded-md px-2 py-1.5 text-ink-100 text-sm fon-dili text-right"
            disabled={submitting}
          />
        </label>
      </div>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">
          Not (ops.)
        </span>
        <input
          type="text"
          maxLength={280}
          value={s.note}
          onChange={(e) => set("note", e.target.value)}
          placeholder="örn. Kasım maaşıyla alındı"
          className="mt-1 w-full bg-ink-900 border border-ink-700 focus:border-accent-500 rounded-md px-2 py-1.5 text-ink-100 text-sm"
          disabled={submitting}
        />
      </label>

      {err && (
        <p className="text-loss-400 text-xs leading-snug">{err}</p>
      )}
      {ok && !err && (
        <p className="text-gain-400 text-xs leading-snug">{ok}</p>
      )}

      <div className="flex justify-end pt-1">
        <button
          type="submit"
          disabled={submitting}
          className="bg-accent-500 hover:bg-accent-400 disabled:opacity-50 disabled:cursor-not-allowed text-ink-900 font-semibold text-sm px-4 py-1.5 rounded-md transition"
        >
          {submitting ? "Ekleniyor…" : "Lot ekle"}
        </button>
      </div>
    </form>
  );
}
